import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { LevelBadge } from "@/components/LevelBadge";
import { HandHeart, Plus, Lock, ArrowUpRight, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function PrayerCircle() {
  const [prayers, setPrayers] = useState([]);
  const [body, setBody] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [answeringId, setAnsweringId] = useState(null);
  const [testimony, setTestimony] = useState("");

  const load = () => {
    api.get("/circle/prayers")
      .then((r) => { setPrayers(r.data); setLocked(false); })
      .catch((e) => { if (e.response?.status === 403) setLocked(true); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/circle/prayers", { body, anonymous });
      setPrayers((p) => [data, ...p]); setBody(""); setShow(false);
      toast.success("Lifted up within the Circle.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pray = async (id) => {
    try {
      const { data } = await api.post(`/circle/prayers/${id}/pray`);
      setPrayers((p) => p.map((x) => x.id === id ? { ...x, pray_count: data.pray_count, prayed: data.prayed } : x));
    } catch { toast.error("Try again"); }
  };

  const markAnswered = async (id) => {
    if (!testimony.trim()) { toast.error("Share a short praise first"); return; }
    try {
      const { data } = await api.post(`/circle/prayers/${id}/answer`, { testimony });
      setPrayers((p) => p.map((x) => x.id === id ? { ...x, answered: true, testimony: data.testimony } : x));
      setAnsweringId(null); setTestimony("");
      toast.success("Praise God! Testimony shared with the Circle.");
    } catch { toast.error("Could not update"); }
  };

  if (loading) return <div className="min-h-screen bg-bone pt-32" />;

  if (locked) {
    return (
      <div className="min-h-screen bg-bone pt-32 pb-24">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <Reveal>
            <Lock size={40} className="mx-auto text-charcoal/40" strokeWidth={1.4} />
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-6">The Gold Prayer Circle</h1>
            <p className="font-sans text-charcoal/60 mt-4">
              A private, intimate space for our Gold & Platinum partners to pray for one another.
              Give $500 or more to join the Circle.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <LevelBadge level="gold" size="md" /><LevelBadge level="platinum" size="md" />
            </div>
            <Link to="/supporter" data-testid="circle-give-cta"
              className="group inline-flex items-center gap-3 mt-10 bg-charcoal text-bone px-9 py-4 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">
              Support the Ministry <ArrowUpRight size={16} className="hover-arrow" />
            </Link>
          </Reveal>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-32 pb-24">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Gold & Platinum Only</span>
                <LevelBadge level="gold" size="sm" />
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4">The Prayer Circle</h1>
              <p className="font-sans text-charcoal/60 mt-4 max-w-xl">An intimate space for our most faithful partners to carry one another before the Lord.</p>
            </div>
            <button onClick={() => setShow(!show)} data-testid="new-circle-prayer-btn"
              className="inline-flex items-center gap-2 bg-charcoal text-bone px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">
              <Plus size={14} /> Share a Request
            </button>
          </div>
        </Reveal>

        {show && (
          <form onSubmit={submit} className="mt-10 border border-gold/30 bg-gold/5 p-8 space-y-6" data-testid="circle-prayer-form">
            <textarea placeholder="What can the Circle pray for?" rows={4} value={body} onChange={(e) => setBody(e.target.value)} required data-testid="circle-prayer-body"
              className="w-full border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-gold resize-none" />
            <label className="flex items-center gap-3 font-sans text-sm text-charcoal/70 cursor-pointer">
              <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="accent-gold w-4 h-4" /> Post anonymously
            </label>
            <button disabled={busy} data-testid="circle-prayer-submit" className="bg-charcoal text-bone px-10 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">{busy ? "Posting..." : "Share Request"}</button>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
          {prayers.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.06}>
              <div className={`border p-8 h-full flex flex-col justify-between ${p.answered ? "border-gold bg-gold/10" : "border-gold/20"}`} data-testid={`circle-prayer-${p.id}`}>
                <div>
                  {p.answered && (
                    <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gold mb-4">
                      <Sparkles size={13} strokeWidth={1.5} /> Answered Prayer
                    </span>
                  )}
                  <p className="font-serif text-2xl leading-snug text-charcoal/90">"{p.body}"</p>
                  {p.answered && p.testimony && (
                    <div className="mt-5 border-l-2 border-gold pl-4">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">Praise Testimony</span>
                      <p className="font-sans text-charcoal/70 mt-1 leading-relaxed">{p.testimony}</p>
                    </div>
                  )}
                </div>
                <div className="mt-8 pt-6 border-t border-charcoal/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-xs uppercase tracking-widest text-charcoal/50 block">{p.author}</span>
                      <span className="font-mono text-[10px] text-charcoal/40">{p.pray_count} praying</span>
                    </div>
                    {!p.answered && (
                      <button onClick={() => pray(p.id)} data-testid={`circle-pray-btn-${p.id}`}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${p.prayed ? "bg-gold text-navy" : "border border-charcoal/30 hover:bg-charcoal hover:text-bone"}`}>
                        <HandHeart size={15} strokeWidth={1.5} /> {p.prayed ? "Praying" : "I'll Pray"}
                      </button>
                    )}
                  </div>
                  {p.is_author && !p.answered && (
                    answeringId === p.id ? (
                      <div className="mt-5" data-testid={`circle-answer-form-${p.id}`}>
                        <textarea value={testimony} onChange={(e) => setTestimony(e.target.value)} rows={2}
                          placeholder="Share how God answered..." data-testid="circle-answer-testimony"
                          className="w-full border border-charcoal/20 bg-transparent p-3 font-sans text-sm focus:outline-none focus:border-gold resize-none" />
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => markAnswered(p.id)} data-testid={`circle-answer-submit-${p.id}`}
                            className="inline-flex items-center gap-2 bg-gold text-charcoal px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-gold/80 transition-colors">
                            <Check size={14} /> Share Praise
                          </button>
                          <button onClick={() => { setAnsweringId(null); setTestimony(""); }} className="font-mono text-xs uppercase tracking-widest text-charcoal/50 px-2">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setAnsweringId(p.id); setTestimony(""); }} data-testid={`circle-mark-answered-${p.id}`}
                        className="mt-4 font-mono text-xs uppercase tracking-widest text-gold hover:underline flex items-center gap-2">
                        <Check size={14} /> Mark as Answered
                      </button>
                    )
                  )}
                </div>
              </div>
            </Reveal>
          ))}
          {prayers.length === 0 && <p className="font-sans text-charcoal/50 col-span-2">The Circle is quiet. Be the first to share.</p>}
        </div>
      </div>
    </div>
  );
}
